/* eslint-disable react-refresh/only-export-components -- context + hook + error class co-located with provider */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { GuestStepUpDialog, type GuestStepUpPhase } from '@/components/guest/GuestStepUpDialog';
import { postStepUp } from './guest-api';

/** Thrown by the Promise `useGuestStepUp()` returns when the guest cancels or dismisses the dialog. */
export class StepUpCancelled extends Error {
  constructor() {
    super('Step-up cancelled');
    this.name = 'StepUpCancelled';
  }
}

interface PendingStepUp {
  resolve: (code: string) => void;
  reject: (err: Error) => void;
}

type DialogState = { open: false } | { open: true; phase: GuestStepUpPhase };

const GuestStepUpContext = createContext<(() => Promise<string>) | null>(null);

/** Resolves a fresh 6-digit code once the guest enters it; rejects `StepUpCancelled` on cancel. */
export function useGuestStepUp(): () => Promise<string> {
  const requestStepUp = useContext(GuestStepUpContext);
  if (!requestStepUp) throw new Error('useGuestStepUp must be used within a GuestStepUpProvider');
  return requestStepUp;
}

export interface GuestStepUpProviderProps {
  children: ReactNode;
  /**
   * Called when the backend refuses the step-up request itself (not a cancel) — per the shell's
   * Interaction Flows, a refused step-up ends the whole guest session, not just this one dialog.
   * Not part of the task doc's literal hook shape (which only covers `useGuestStepUp()`); added
   * because nothing else in this module's scope can signal the shell to transition to `invalid`.
   */
  onRefused?: () => void;
}

export function GuestStepUpProvider({ children, onRefused }: GuestStepUpProviderProps) {
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const pendingRef = useRef<PendingStepUp | null>(null);

  const sendCode = useCallback(async () => {
    // codex-qa C4 (MR !504): `pendingRef` is the request this response belongs to. Cancel clears
    // it synchronously, so a response that lands after cancel (or after a later request superseded
    // this one) has nothing left to resolve — ignore it instead of reopening the dialog.
    const owner = pendingRef.current;
    setDialog({ open: true, phase: 'sending' });
    const answer = await postStepUp();
    if (pendingRef.current !== owner) return;
    if (answer.kind === 'sent') {
      setDialog({ open: true, phase: 'enterCode' });
    } else if (answer.kind === 'wait') {
      setDialog({ open: true, phase: 'wait' });
    } else if (answer.kind === 'unavailable') {
      setDialog({ open: true, phase: 'unavailable' });
    } else {
      pendingRef.current?.reject(new Error('Step-up request was refused'));
      pendingRef.current = null;
      setDialog({ open: false });
      onRefused?.();
    }
  }, [onRefused]);

  const requestStepUp = useCallback((): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      void sendCode();
    });
  }, [sendCode]);

  const handleSubmit = useCallback((code: string) => {
    pendingRef.current?.resolve(code);
    pendingRef.current = null;
    setDialog({ open: false });
  }, []);

  const handleCancel = useCallback(() => {
    pendingRef.current?.reject(new StepUpCancelled());
    pendingRef.current = null;
    setDialog({ open: false });
  }, []);

  const handleRetry = useCallback(() => {
    void sendCode();
  }, [sendCode]);

  return (
    <GuestStepUpContext.Provider value={requestStepUp}>
      {children}
      <GuestStepUpDialog
        open={dialog.open}
        phase={dialog.open ? dialog.phase : 'sending'}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onRetry={handleRetry}
      />
    </GuestStepUpContext.Provider>
  );
}
